package com.appsyde.shared.app.presentation.account

import com.appsyde.shared.core.presentation.BaseViewModel
import com.appsyde.shared.modules.account.domain.model.Account
import com.appsyde.shared.modules.account.domain.model.BillingStatus
import com.appsyde.shared.modules.account.domain.model.RequestDeletionResult
import com.appsyde.shared.modules.account.domain.usecase.CreateCheckoutSessionUseCase
import com.appsyde.shared.modules.account.domain.usecase.CreateCustomerPortalSessionUseCase
import com.appsyde.shared.modules.account.domain.usecase.GetAccountUseCase
import com.appsyde.shared.modules.account.domain.usecase.GetBillingStatusUseCase
import com.appsyde.shared.modules.account.domain.usecase.GetMeUseCase
import com.appsyde.shared.modules.account.domain.usecase.RequestAccountDeletionUseCase
import com.appsyde.shared.modules.account.domain.usecase.UpdateAccountSignatureUseCase
import com.appsyde.shared.modules.account.domain.usecase.UpdateAccountToneUseCase
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

private const val DEBOUNCE_SIGNATURE_MS = 400L

/** API tone values. */
private val TONE_VALUES = listOf("PROFESSIONAL", "BALANCED", "WARM")

fun toneToIndex(tone: String): Int = when (tone.uppercase()) {
    "PROFESSIONAL" -> 0
    "BALANCED" -> 1
    "WARM" -> 2
    else -> 1
}

fun indexToTone(index: Int): String = TONE_VALUES[index.coerceIn(0, 2)]

class AccountViewModel(
    private val getAccountUseCase: GetAccountUseCase,
    private val getMeUseCase: GetMeUseCase,
    private val updateAccountToneUseCase: UpdateAccountToneUseCase,
    private val updateAccountSignatureUseCase: UpdateAccountSignatureUseCase,
    private val requestAccountDeletionUseCase: RequestAccountDeletionUseCase,
    private val getBillingStatusUseCase: GetBillingStatusUseCase,
    private val createCheckoutSessionUseCase: CreateCheckoutSessionUseCase,
    private val createCustomerPortalSessionUseCase: CreateCustomerPortalSessionUseCase
) : BaseViewModel() {

    data class State(
        val account: Account? = null,
        val userEmail: String? = null,
        val userName: String? = null,
        /** Id du compte utilisateur courant (GET /me → user.id) — pour sidebar « Reprendre » */
        val accountUserId: String? = null,
        val role: String? = null,
        val toneIndex: Int = 1,
        val signatureEnabled: Boolean = true,
        val signatureValue: String = "",
        val isLoading: Boolean = false,
        val deleteRequestInProgress: Boolean = false,
        val deleteRequestMessage: String? = null,
        val billingStatus: BillingStatus? = null,
        val billingLoading: Boolean = false,
        val checkoutInProgress: Boolean = false,
        val portalInProgress: Boolean = false
    )

    private val _state = MutableStateFlow(State())
    val state: StateFlow<State> = _state.asStateFlow()

    private var signatureSaveJob: Job? = null
    private fun cancelSignatureDebounce() {
        signatureSaveJob?.cancel()
        signatureSaveJob = null
    }

    fun loadAccount() {
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true) }
            val me = getMeUseCase()
            val account = getAccountUseCase()
            _state.update {
                it.copy(
                    account = account,
                    userEmail = me?.user?.email,
                    userName = me?.user?.name,
                    accountUserId = me?.user?.id,
                    role = me?.role,
                    toneIndex = account?.tone?.let(::toneToIndex) ?: 1,
                    signatureEnabled = account?.signatureEnabled ?: true,
                    signatureValue = account?.signatureValue.orEmpty(),
                    isLoading = false
                )
            }
        }
    }

    fun setToneIndex(index: Int) {
        val safeIndex = index.coerceIn(0, 2)
        _state.update { it.copy(toneIndex = safeIndex) }
        viewModelScope.launch {
            updateAccountToneUseCase(indexToTone(safeIndex))
        }
    }

    /** Met à jour signature (toggle ou autre) et envoie immédiatement au serveur. */
    fun setSignature(signatureEnabled: Boolean, signatureValue: String) {
        cancelSignatureDebounce()
        _state.update {
            it.copy(signatureEnabled = signatureEnabled, signatureValue = signatureValue)
        }
        viewModelScope.launch {
            updateAccountSignatureUseCase(signatureEnabled, signatureValue)
        }
    }

    /** Met à jour le texte de la signature (state immédiat, requête débattue). */
    fun setSignatureText(value: String) {
        _state.update { it.copy(signatureValue = value) }
        signatureSaveJob?.cancel()
        signatureSaveJob = viewModelScope.launch {
            delay(DEBOUNCE_SIGNATURE_MS)
            val s = _state.value
            updateAccountSignatureUseCase(s.signatureEnabled, s.signatureValue)
        }
    }

    fun requestAccountDeletion() {
        viewModelScope.launch {
            _state.update { it.copy(deleteRequestInProgress = true, deleteRequestMessage = null) }
            val result = requestAccountDeletionUseCase()
            when (result) {
                is RequestDeletionResult.Ok ->
                    _state.update {
                        it.copy(
                            deleteRequestInProgress = false,
                            deleteRequestMessage = "Un email de confirmation a été envoyé."
                        )
                    }
                is RequestDeletionResult.AlreadyPending ->
                    _state.update {
                        it.copy(
                            deleteRequestInProgress = false,
                            deleteRequestMessage = "Une demande est déjà en attente."
                        )
                    }
                is RequestDeletionResult.Error ->
                    _state.update {
                        it.copy(
                            deleteRequestInProgress = false,
                            deleteRequestMessage = result.message ?: "Une erreur est survenue."
                        )
                    }
            }
        }
    }

    fun clearDeleteRequestMessage() {
        _state.update { it.copy(deleteRequestMessage = null) }
    }

    fun loadBillingStatus() {
        viewModelScope.launch {
            _state.update { it.copy(billingLoading = true) }
            val status = getBillingStatusUseCase()
            _state.update {
                it.copy(billingStatus = status, billingLoading = false)
            }
        }
    }

    /** Returns checkout URL to redirect to, or null on error. */
    suspend fun startCheckout(): String? {
        _state.update { it.copy(checkoutInProgress = true) }
        val session = createCheckoutSessionUseCase()
        _state.update { it.copy(checkoutInProgress = false) }
        return session?.url
    }

    /** Returns customer portal URL to redirect to (résilier / gérer abonnement), or null on error. */
    suspend fun startCustomerPortal(): String? {
        _state.update { it.copy(portalInProgress = true) }
        val response = createCustomerPortalSessionUseCase()
        _state.update { it.copy(portalInProgress = false) }
        return response?.url
    }
}
